import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';
import type { StoreApi } from 'zustand';
import { createStore, useStore } from 'zustand';

// Generic, feature-agnostic per-tile navigation channel.
//
// Tiles render in a different React subtree (UrlTileRenderer) than the views
// that own their data (e.g. a board). This channel lets the data owner PUBLISH
// prev/next handlers for an open tile, keyed by the tile's stable key, and lets
// the tile (or its chrome) CONSUME them. Both sides only need a common ancestor
// mounting UrlTileNavigationProvider — no DOM portal, no per-tile plumbing.
//
// A handler set to undefined means "no target in that direction" (disabled).
export type UrlTileNavigation = {
    readonly onNext?: () => void;
    readonly onPrevious?: () => void;
};

type UrlTileNavigationStore = {
    readonly navigationByTileKey: ReadonlyMap<string, UrlTileNavigation>;
    readonly register: (tileKey: string, navigation: UrlTileNavigation) => void;
    readonly unregister: (tileKey: string) => void;
};

const createUrlTileNavigationStore = (): StoreApi<UrlTileNavigationStore> =>
    createStore<UrlTileNavigationStore>((set) => ({
        navigationByTileKey: new Map(),
        register: (tileKey, navigation) => {
            set((state) => {
                const next = new Map(state.navigationByTileKey);
                next.set(tileKey, navigation);

                return { navigationByTileKey: next };
            });
        },
        unregister: (tileKey) => {
            set((state) => {
                if (!state.navigationByTileKey.has(tileKey)) {
                    return state;
                }
                const next = new Map(state.navigationByTileKey);
                next.delete(tileKey);

                return { navigationByTileKey: next };
            });
        },
    }));

const UrlTileNavigationContext = createContext<StoreApi<UrlTileNavigationStore> | null>(null);

export const UrlTileNavigationProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const storeRef = useRef<StoreApi<UrlTileNavigationStore> | null>(null);
    storeRef.current ??= createUrlTileNavigationStore();

    return <UrlTileNavigationContext.Provider value={storeRef.current}>{children}</UrlTileNavigationContext.Provider>;
};

const useUrlTileNavigationStore = (): StoreApi<UrlTileNavigationStore> => {
    const store = useContext(UrlTileNavigationContext);

    if (store === null) {
        throw new Error('useRegisterTileNavigation/useTileNavigation must be used within a UrlTileNavigationProvider');
    }

    return store;
};

// Data owner side: publish prev/next handlers for an open tile. Pass undefined
// to publish nothing (e.g. when no tile is open) — the entry is removed.
export const useRegisterTileNavigation = (
    tileKey: string | undefined,
    navigation: UrlTileNavigation | undefined,
): void => {
    const store = useUrlTileNavigationStore();
    const onNext = navigation?.onNext;
    const onPrevious = navigation?.onPrevious;

    useEffect(() => {
        if (is.undefined(tileKey) || is.undefined(navigation)) {
            return;
        }

        store.getState().register(tileKey, { onNext, onPrevious });

        return () => store.getState().unregister(tileKey);
    }, [store, tileKey, navigation, onNext, onPrevious]);
};

// Tile side: read the navigation published for this tile, if any.
export const useTileNavigation = (tileKey: string | undefined): UrlTileNavigation | undefined => {
    const store = useUrlTileNavigationStore();

    return useStore(store, (state) => (is.undefined(tileKey) ? undefined : state.navigationByTileKey.get(tileKey)));
};
